// Slice 12 — BesideU Admin overview landing (DESIGN §6.6). At-a-glance stats
// across all customer orgs. Lives at /admin (shell index). The other tabs
// own the detail / mutation surfaces; this page is purely a dashboard.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type {
  AdminAuditEntry,
  AdminCgcMemberSummary,
  AdminOrgSummary,
} from '@besideu/shared/admin';
import { api } from '@/lib/api.ts';

export function AdminOverview() {
  const orgs = useQuery({
    queryKey: ['admin', 'orgs'],
    queryFn: () => api.get<{ orgs: AdminOrgSummary[] }>('/api/admin/orgs'),
  });
  const cgcMembers = useQuery({
    queryKey: ['admin', 'cgc-members'],
    queryFn: () => api.get<{ members: AdminCgcMemberSummary[] }>('/api/admin/cgc-members'),
  });
  const audit = useQuery({
    queryKey: ['admin', 'audit-log'],
    queryFn: () => api.get<{ entries: AdminAuditEntry[] }>('/api/admin/audit-log?limit=10'),
  });

  const orgStats = useMemo(() => computeOrgStats(orgs.data?.orgs ?? []), [orgs.data]);
  const cgcStats = useMemo(
    () => computeCgcStats(cgcMembers.data?.members ?? []),
    [cgcMembers.data],
  );

  return (
    <section className="space-y-10">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Metadata-only access across all customer organisations (DESIGN §6.6).
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Pending approvals"
          value={orgStats.pending}
          loading={orgs.isPending}
          href={orgStats.pending > 0 ? '/admin/orgs?status=Pending' : undefined}
          tone={orgStats.pending > 0 ? 'attention' : 'neutral'}
        />
        <StatCard
          label="Active organisations"
          value={orgStats.active}
          loading={orgs.isPending}
          href="/admin/orgs?status=Active"
        />
        <StatCard
          label="Suspended organisations"
          value={orgStats.suspended}
          loading={orgs.isPending}
          href={orgStats.suspended > 0 ? '/admin/orgs?status=Suspended' : undefined}
        />
        <StatCard
          label="CGC members (active)"
          value={cgcStats.active}
          sublabel={cgcStats.suspended > 0 ? `${cgcStats.suspended} suspended` : undefined}
          loading={cgcMembers.isPending}
          href="/admin/cgc-members"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionHeader title="Recent actions" linkTo="/admin/audit-log" linkLabel="View all" />
          {audit.isPending ? (
            <Placeholder>Loading…</Placeholder>
          ) : audit.error ? (
            <Placeholder error>Could not load audit log.</Placeholder>
          ) : (audit.data?.entries ?? []).length === 0 ? (
            <Placeholder>No actions logged yet.</Placeholder>
          ) : (
            <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
              {audit.data.entries.slice(0, 5).map((e) => (
                <li key={e.id} className="px-4 py-3 text-sm">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium">{formatAction(e.action)}</span>
                    <span className="text-xs whitespace-nowrap text-muted-foreground">
                      {new Date(e.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {e.actorAdminName ??
                      (e.actorAdminId ? `${e.actorAdminId.slice(0, 8)}…` : 'System')}
                    {e.targetOrgName ? ` · ${e.targetOrgName}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <SectionHeader title="Quick actions" />
          <div className="mt-3 flex flex-col gap-2">
            <QuickLink
              to="/admin/orgs?status=Pending"
              label="Review pending sign-ups"
              count={orgStats.pending}
            />
            <QuickLink to="/admin/prompts" label="Draft a system prompt" />
            <QuickLink to="/admin/cgc-members" label="Manage CGC roster" />
          </div>
        </div>
      </div>
    </section>
  );
}

function computeOrgStats(orgs: AdminOrgSummary[]) {
  let pending = 0;
  let active = 0;
  let suspended = 0;
  for (const o of orgs) {
    if (o.status === 'Pending') pending += 1;
    else if (o.status === 'Active') active += 1;
    else if (o.status === 'Suspended') suspended += 1;
  }
  return { pending, active, suspended };
}

function computeCgcStats(members: AdminCgcMemberSummary[]) {
  let active = 0;
  let suspended = 0;
  for (const m of members) {
    if (m.status === 'Active') active += 1;
    else suspended += 1;
  }
  return { active, suspended };
}

function StatCard({
  label,
  value,
  sublabel,
  loading,
  href,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  sublabel?: string | undefined;
  loading: boolean;
  href?: string | undefined;
  tone?: 'neutral' | 'attention' | undefined;
}) {
  const className = [
    'rounded-lg border bg-background p-4 transition',
    tone === 'attention' ? 'border-traffic-amber/50 bg-traffic-amber/5' : 'border-border',
    href ? 'hover:border-foreground/40' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const body = (
    <>
      <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">{label}</p>
      <p className="mt-2 text-3xl font-semibold tracking-tight">
        {loading ? <span className="text-muted-foreground">—</span> : value}
      </p>
      {sublabel ? <p className="mt-1 text-xs text-muted-foreground">{sublabel}</p> : null}
    </>
  );

  if (href) {
    return (
      <Link to={href} className={className}>
        {body}
      </Link>
    );
  }
  return <div className={className}>{body}</div>;
}

function SectionHeader({
  title,
  linkTo,
  linkLabel,
}: {
  title: string;
  linkTo?: string | undefined;
  linkLabel?: string | undefined;
}) {
  return (
    <div className="flex items-baseline justify-between">
      <h2 className="text-lg font-semibold">{title}</h2>
      {linkTo && linkLabel ? (
        <Link to={linkTo} className="text-sm text-muted-foreground underline hover:text-foreground">
          {linkLabel}
        </Link>
      ) : null}
    </div>
  );
}

function Placeholder({
  children,
  error,
}: {
  children: React.ReactNode;
  error?: boolean | undefined;
}) {
  return (
    <p className={`mt-3 text-sm ${error ? 'text-traffic-red' : 'text-muted-foreground'}`}>
      {children}
    </p>
  );
}

function QuickLink({
  to,
  label,
  count,
}: {
  to: string;
  label: string;
  count?: number | undefined;
}) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3 text-sm transition hover:border-foreground/40"
    >
      <span>{label}</span>
      {typeof count === 'number' && count > 0 ? (
        <span className="rounded-full bg-traffic-amber/10 px-2 py-0.5 text-xs font-medium text-traffic-amber-text">
          {count}
        </span>
      ) : (
        <span aria-hidden className="text-muted-foreground">
          →
        </span>
      )}
    </Link>
  );
}

// Turn `Org_Suspended` into `Org suspended` for display. Audit actions are
// SCREAMING_SNAKE in the DB and Title_Snake here — neither is great for
// at-a-glance reading.
function formatAction(action: string): string {
  const lower = action.toLowerCase().replace(/_/g, ' ');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
